/**
 * Замер моргания активного табло (IMGM-1435).
 *
 * Отвечает на вопрос, который нельзя решить чтением кода: что именно происходит в момент
 * моргания — меняется активное табло (тогда виновата строка журнала), или табло стоит на месте,
 * а у объектов канвы прыгает прозрачность (тогда виноват цикл показа), или объекты пропадают
 * поштучно (тогда виноваты прерванные замены картинок).
 *
 * Пишет в кольцевой буфер, ничего не отправляет и по умолчанию выключен: включается параметром
 * `?diag=1` в ссылке оверлея или вызовом `overlayDiag.start()` из консоли. Выгрузка —
 * `overlayDiag.dump()` (таблица в консоли) и `overlayDiag.toJson()` (текст, чтобы переслать).
 */

import type { Canvas, StaticCanvas } from 'fabric/fabric-impl';
import supabase from 'modules/core/supabase/supabase';

const RING_SIZE = 3_000;
const DEFAULT_INTERVAL_MS = 1_000;

export interface DiagnosticEntry {
  /** Миллисекунды от старта замера — так проще читать частоту. */
  at: number;
  type: string;
  payload?: unknown;
}

interface CanvasSample {
  objects: number;
  /** Сколько объектов в каждой корзине прозрачности: `1` — видимые, `0` — невидимые. */
  opacity: Record<string, number>;
  width: number;
  height: number;
}

type CanvasGetter = () => Canvas | StaticCanvas | undefined;

function sampleCanvas(canvas: Canvas | StaticCanvas): CanvasSample {
  const objects = canvas.getObjects();
  const opacity: Record<string, number> = {};

  objects.forEach((object) => {
    const bucket = (Math.round((object.opacity ?? 1) * 10) / 10).toFixed(1);
    opacity[bucket] = (opacity[bucket] ?? 0) + 1;
  });

  return {
    objects: objects.length,
    opacity,
    width: canvas.getWidth(),
    height: canvas.getHeight(),
  };
}

class BoardDiagnostics {
  private entries: DiagnosticEntry[] = [];
  private startedAt = 0;
  private timer?: ReturnType<typeof setInterval>;
  private unsubscribers: (() => void)[] = [];
  private readonly canvases = new Map<string, CanvasGetter>();
  private lastSample = '';

  get isRunning(): boolean {
    return this.timer !== undefined;
  }

  /** Канвы регистрируют себя сами: без доступа к объектам Fabric замерять нечего. */
  public registerCanvas(name: string, getCanvas: CanvasGetter) {
    this.canvases.set(name, getCanvas);
  }

  public unregisterCanvas(name: string) {
    this.canvases.delete(name);
  }

  /** Запись события. Пока замер выключен — ничего не стоит. */
  public record(type: string, payload?: unknown) {
    if (!this.isRunning) return;

    this.entries.push({ at: Date.now() - this.startedAt, type, payload });
    if (this.entries.length > RING_SIZE) this.entries.shift();
  }

  public start(intervalMs = DEFAULT_INTERVAL_MS) {
    if (this.isRunning) return;

    this.startedAt = Date.now();
    this.entries = [];
    this.lastSample = '';
    this.timer = setInterval(() => this.sample(), intervalMs);
    this.subscribeToRealtime();

    console.warn('[overlayDiag] замер включён. Выгрузка: overlayDiag.dump() / overlayDiag.toJson()');
    this.record('diag:start', { intervalMs });
  }

  public stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [];
    console.warn('[overlayDiag] замер выключен');
  }

  public entriesSnapshot(): DiagnosticEntry[] {
    return [...this.entries];
  }

  public dump(): DiagnosticEntry[] {
    const entries = this.entriesSnapshot();
    console.table(entries.map((entry) => ({ at: entry.at, type: entry.type, payload: JSON.stringify(entry.payload ?? null) })));
    return entries;
  }

  public toJson(): string {
    return JSON.stringify(this.entriesSnapshot());
  }

  /**
   * Снимок канв раз в интервал. Повторы схлопываются: пока ничего не меняется, буфер
   * не забивается, а любое изменение видно строкой с новым временем.
   */
  private sample() {
    const snapshot: Record<string, CanvasSample> = {};

    this.canvases.forEach((getCanvas, name) => {
      const canvas = getCanvas();
      if (canvas) snapshot[name] = sampleCanvas(canvas);
    });

    const serialized = JSON.stringify(snapshot);
    if (serialized === this.lastSample) return;

    this.lastSample = serialized;
    this.record('canvas', snapshot);
  }

  /**
   * Что приезжает по realtime. Подписки на те же поля, что слушает оверлей, — так в одной
   * ленте видно и причину (пришло новое значение), и следствие (перезапуск показа).
   */
  private subscribeToRealtime() {
    const { matchChronology, broadcast } = supabase;

    const onBoard = (id: number | null) => this.record('realtime:activeBoardId', id);
    const onTime = ({ seconds, period }: { seconds: number; period: number }) => this.record('realtime:time', { seconds, period });
    const onStatus = (status: string) => this.record('realtime:channel', status);

    // Пропажа строки трансляции — единственный путь, которым код гасит табло сам: следом
    // он же обнуляет период в 1. Если тайм скачет 1 ↔ 2, ответ будет виден именно здесь.
    const onBroadcast = (data: { id: number; matchId: number; templateId: number | null } | undefined) =>
      this.record('broadcast:data', data ? { rowId: data.id, matchId: data.matchId, templateId: data.templateId } : null);

    matchChronology.activeBoardId.subscribe(onBoard);
    matchChronology.currentTime.subscribe(onTime);
    matchChronology.connectionStatus.subscribe(onStatus);
    broadcast.data.subscribe(onBroadcast);

    this.unsubscribers.push(() => matchChronology.activeBoardId.unsubscribe(onBoard));
    this.unsubscribers.push(() => matchChronology.currentTime.unsubscribe(onTime));
    this.unsubscribers.push(() => matchChronology.connectionStatus.unsubscribe(onStatus));
    this.unsubscribers.push(() => broadcast.data.unsubscribe(onBroadcast));
  }
}

export const boardDiagnostics = new BoardDiagnostics();

/** Включение из ссылки: `...&diag=1`. Зовётся один раз, из точки входа оверлея. */
export function startDiagnosticsIfRequested() {
  if (typeof window === 'undefined') return;

  (window as unknown as { overlayDiag: BoardDiagnostics }).overlayDiag = boardDiagnostics;

  const params = new URLSearchParams(window.location.search);
  if (params.get('diag') === '1') boardDiagnostics.start();
}
